// OpsMockDataTab — administration des données TEST d'AYA Search (v5.6.0).
// Désactiver (conserve en base) · Supprimer définitivement (is_mock:true uniquement) · Réinjecter.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CAT_LABELS = {
  commerce: "Commerce", coiffure: "Coiffure", beaute: "Beauté", restaurant: "Food / Restaurants",
  sante: "Santé", pharmacie: "Pharmacies", justice: "Justice", hotel: "Hôtellerie",
  transport: "Transport", emploi: "Emploi", administration: "Administration",
  finance: "Finance", divertissement: "Divertissement", services: "Services / Artisans",
};

export const OpsMockDataTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const headers = { "X-Admin-Token": token };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/mock-data/stats`, { headers });
      setStats(r.data);
    } catch (e) {
      toast.error("Impossible de charger les stats mock");
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/mock-data/toggle`, { enabled: !stats.enabled }, { headers });
      toast.success(r.data.enabled ? "Données TEST réactivées dans Search" : "Données TEST désactivées (conservées en base)");
      await load();
    } catch { toast.error("Échec du toggle"); } finally { setBusy(false); }
  };

  const reseed = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/mock-data/reseed`, {}, { headers });
      toast.success(`${r.data.inserted || 0} données TEST réinjectées`);
      await load();
    } catch { toast.error("Échec du reseed"); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/mock-data`, { headers });
      toast.success(`${r.data.deleted} données de test supprimées définitivement`);
      setConfirmOpen(false);
      await load();
    } catch { toast.error("Échec de la suppression"); } finally { setBusy(false); }
  };

  if (!stats) return <div style={{ color: "#9DB4A6", padding: 20 }}>Chargement…</div>;

  const cats = Object.entries(stats.by_category || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div data-testid="ops-mock-data-tab" style={{ maxWidth: 720 }}>
      <h2 style={{ margin: "0 0 4px" }}>🧪 Données Test (mock) — AYA Search</h2>
      <p style={{ color: "#9DB4A6", fontSize: 13, marginTop: 0 }}>
        Documents <code>is_mock:true</code> de la collection <code>search_documents</code>.
        Ils portent le badge <b>TEST</b> dans Search et ne sont jamais confondus avec des données réelles.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "14px 0" }}>
        <div style={S.stat}>
          <div style={S.statVal} data-testid="mock-total">{stats.total}</div>
          <div style={S.statLabel}>documents test</div>
        </div>
        <div style={S.stat}>
          <div style={{ ...S.statVal, color: stats.enabled ? "#34D399" : "#F87171" }} data-testid="mock-enabled-state">
            {stats.enabled ? "ACTIVES" : "DÉSACTIVÉES"}
          </div>
          <div style={S.statLabel}>dans Search {!stats.env_enabled && "(forcé OFF par env)"}</div>
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="mock-category-table">
        <tbody>
          {cats.map(([c, n]) => (
            <tr key={c} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <td style={{ padding: "6px 4px", color: "#CFE5D6" }}>{CAT_LABELS[c] || c}</td>
              <td style={{ padding: "6px 4px", textAlign: "right", fontWeight: 800 }}>{n}</td>
            </tr>
          ))}
          {cats.length === 0 && <tr><td style={{ padding: 10, color: "#9DB4A6" }}>Aucune donnée test en base.</td></tr>}
        </tbody>
      </table>

      <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
        {stats.total > 0 && (
          <button className="ops-btn" onClick={toggle} disabled={busy} data-testid="mock-toggle-btn"
            style={{ borderColor: stats.enabled ? "#F59E0B" : "#34D399", color: stats.enabled ? "#F59E0B" : "#34D399" }}>
            {stats.enabled ? "⏸ DÉSACTIVER LES DONNÉES TEST" : "▶️ RÉACTIVER LES DONNÉES TEST"}
          </button>
        )}
        {stats.total > 0 && (
          <button className="ops-btn" onClick={() => setConfirmOpen(true)} disabled={busy} data-testid="mock-delete-btn"
            style={{ borderColor: "#EF4444", color: "#F87171" }}>
            🗑️ SUPPRIMER LES DONNÉES MOCK
          </button>
        )}
        {stats.total === 0 && (
          <button className="ops-btn" onClick={reseed} disabled={busy} data-testid="mock-reseed-btn"
            style={{ borderColor: "#34D399", color: "#34D399" }}>
            ♻️ RÉINJECTER LES DONNÉES TEST
          </button>
        )}
      </div>

      {confirmOpen && (
        <div style={S.overlay} data-testid="mock-delete-confirm-modal">
          <div style={S.modal}>
            <h3 style={{ marginTop: 0 }}>SUPPRIMER LES DONNÉES DE TEST ?</h3>
            <p style={{ color: "#F87171", fontWeight: 700 }}>{stats.total} données de test seront supprimées définitivement.</p>
            <ul style={{ color: "#CFE5D6", fontSize: 13, maxHeight: 200, overflowY: "auto" }}>
              {cats.map(([c, n]) => <li key={c}>{CAT_LABELS[c] || c} : {n}</li>)}
            </ul>
            <p style={{ color: "#9DB4A6", fontSize: 12 }}>
              Seuls les documents <code>is_mock:true</code> sont concernés. Les utilisateurs, vrais commerces,
              commandes, réservations et paiements ne sont JAMAIS touchés.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
              <button className="ops-btn" onClick={() => setConfirmOpen(false)} data-testid="mock-delete-cancel">ANNULER</button>
              <button className="ops-btn" onClick={doDelete} disabled={busy} data-testid="mock-delete-confirm"
                style={{ background: "#DC2626", borderColor: "#DC2626", color: "#fff", fontWeight: 800 }}>
                SUPPRIMER DÉFINITIVEMENT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const S = {
  stat: { padding: "10px 18px", borderRadius: 12, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" },
  statVal: { fontSize: 22, fontWeight: 900, color: "#ECF5EE" },
  statLabel: { fontSize: 11, color: "#9DB4A6" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { width: "min(480px, 92vw)", background: "#0B1512", border: "1px solid rgba(239,68,68,0.5)", borderRadius: 16, padding: 20, color: "#ECF5EE" },
};

export default OpsMockDataTab;
